import React, { useState, useEffect, useMemo } from 'react';
import { Bar, Line } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, LineElement, PointElement, Title, Tooltip, Legend } from 'chart.js';
import TimeRangeSelector from './TimeRangeSelector';
import { subYears } from 'date-fns';
import '../styles/PricingPlot.css';
import FilteredPostsDisplay from './FilteredPostsDisplay';
import ProductSelector from './ProductSelector';
import PricingComparisonChart from './PricingComparisonChart';
import { getAssessmentKeywords } from '../utils/assessmentKeywords';

ChartJS.register(CategoryScale, LinearScale, BarElement, LineElement, PointElement, Title, Tooltip, Legend);

const colors = {
    veryReceptive: '#2ecc71',      // Bright green
    moderatelyReceptive: '#82e0aa', // Light green
    neutral: '#d5d8dc',            // Light gray
    notReceptive: '#f1948a',       // Light red
    extremelyNotReceptive: '#e74c3c' // Bright red
};

const thresholds = {
    veryReceptive: 7,
    moderatelyReceptive: 3,
    notReceptive: -3,
    extremelyNotReceptive: -7
};

const willingness = {
    veryReceptive: 1,
    moderatelyReceptive: 0.95,
    neutral: 0.65,
    notReceptive: 0.35,
    extremelyNotReceptive: 0.10
};

const pricePoints = [0, 0.05, 0.1, 0.15, 0.2]; // 0% to 20% price increase

const PricingSummary = ({ categoryCounts, onCategoryClick }) => {
    const total = Object.values(categoryCounts).reduce((a, b) => a + b, 0);
    const receptive = categoryCounts.veryReceptive + categoryCounts.moderatelyReceptive;
    const resistant = categoryCounts.extremelyNotReceptive + categoryCounts.notReceptive;
    
    return (
        <div className="pricing-summary">
            <div 
                className="summary-box"
                style={{ borderLeft: `4px solid ${colors.veryReceptive}` }}
                onClick={() => onCategoryClick(['veryReceptive', 'moderatelyReceptive'])}
            >
                <h2 style={{ color: colors.veryReceptive }}>
                    {((receptive / total) * 100).toFixed(1)}%
                </h2>
                <p>Open to Price Increase</p>
            </div>
            <div 
                className="summary-box"
                style={{ borderLeft: `4px solid ${colors.neutral}` }}
                onClick={() => onCategoryClick(['neutral'])}
            >
                <h2 style={{ color: '#666' }}>
                    {((categoryCounts.neutral / total) * 100).toFixed(1)}%
                </h2>
                <p>Indifferent</p>
            </div>
            <div 
                className="summary-box"
                style={{ borderLeft: `4px solid ${colors.extremelyNotReceptive}` }}
                onClick={() => onCategoryClick(['notReceptive', 'extremelyNotReceptive'])}
            >
                <h2 style={{ color: colors.extremelyNotReceptive }}>
                    {((resistant / total) * 100).toFixed(1)}%
                </h2>
                <p>Price Sensitive</p>
            </div>
        </div>
    );
};

const PricingPlot = ({ posts, title, topics, assessmentData, topicMap,
    // Optional shared product selection: adopted when valid for this
    // component's own selectable topics; selections made here are pushed
    // back up so other sections of the page can pick them up.
    selectedProduct: sharedSelectedProduct, onProductChange }) => {
    const [data, setData] = useState([]);
    const [chartData, setChartData] = useState(null);
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [postsReferenced, setPostsReferenced] = useState([]);
    const [demandData, setDemandData] = useState(null);
    const [timeRange, setTimeRange] = useState('3y');
    // Lazy-initialize from the shared selection (if valid) so the local
    // state is never empty on first render. If it started empty,
    // ProductSelector's own internal auto-select effect (a child
    // component, whose effects run before this component's) would fire
    // first and push its own default back up via onProductChange, always
    // winning the race and clobbering a valid incoming shared selection.
    const [selectedProduct, setSelectedProduct] = useState(() =>
        (sharedSelectedProduct && topicMap?.[sharedSelectedProduct]) ? sharedSelectedProduct : ""
    );
    const [filteredTopicMap, setFilteredTopicMap] = useState({});

    const selectProduct = (productId) => {
        setSelectedProduct(productId);
        if (onProductChange) onProductChange(productId);
    };

    // Helper function to check if a topicMap contains valid product data
    const hasValidProductData = (map) => {
        if (!map || typeof map !== 'object') return false;
        
        // Check if there are any items with valid product data
        return Object.values(map).some(item => 
            item && 
            typeof item === 'object' && 
            item.id && 
            (item.name || item.topic)
        );
    };

    // Get only product topics (for the selector)
    const productTopicsForSelector = useMemo(() => {
        if (!topics || !assessmentData) return {};
        
        // Group assessmentData by topic_id
        const assessmentsByTopic = {};
        assessmentData.forEach(item => {
            const topicId = item.topic_id || item.id;
            if (topicId) {
                if (!assessmentsByTopic[topicId]) {
                    assessmentsByTopic[topicId] = [];
                }
                assessmentsByTopic[topicId].push(item);
            }
        });
        
        // Filter topics to only include those with pricing data
        const validTopics = topics.filter(topic => {
            // Check if this topic has pricing data
            return assessmentsByTopic[topic.id] && 
                   assessmentsByTopic[topic.id].length > 0 &&
                   assessmentsByTopic[topic.id].some(item => item.rating !== null);
        });
        
        console.log('Topics with pricing data:', validTopics.map(t => t.topic || t.name));
        
        // Convert to topicMap format for ProductSelector
        const topicMap = validTopics.reduce((acc, topic) => {
            acc[topic.id] = {
                id: topic.id,
                name: topic.topic || topic.name,
                priority: topic.priority
            };
            return acc;
        }, {});
        
        return topicMap;
    }, [topics, assessmentData]);

    const filterPostsByProduct = (posts) => {
        if (!selectedProduct || !topics) return posts;
        
        const selectedTopic = topics.find(t => t.id === selectedProduct);
        if (!selectedTopic) return posts;
        
        // Return only posts that belong to the selected topic
        return selectedTopic.posts
            .filter(post => 
                post.post_assessments?.some(assessment => 
                    assessment.type === 'pricing' && assessment.rating !== null
                )
            );
    };

    // Process assessment data if available, otherwise fall back to filtering posts
    useEffect(() => {
        // If assessmentData is provided, use it directly
        if (assessmentData && Array.isArray(assessmentData)) {
            console.log('Using provided assessment data for pricing:', assessmentData);
            
            // Filter by time range
            const now = new Date();
            let timeFilteredData = assessmentData.filter(item => {
                if (!item?.time) return false;
                if (timeRange === 'all') return true;
                const date = new Date(item.time * 1000);
                const years = parseInt(timeRange);
                return date >= subYears(now, years);
            });
            
            // If a product is selected, filter assessment data by product ID
            if (selectedProduct) {
                console.log('Filtering assessment data by product ID:', selectedProduct);
                timeFilteredData = timeFilteredData.filter(item => {
                    // Check both topic_id and id fields to handle different formats
                    return (
                        (item.topic_id && item.topic_id === selectedProduct) || 
                        (item.id && item.id === selectedProduct)
                    );
                });
                
                console.log('Filtered assessment data count:', timeFilteredData.length);
            }
            
            setData(timeFilteredData);
        } 
        // Otherwise, fall back to filtering posts directly as before
        else if (Array.isArray(posts) && posts.length > 0) {
            console.log('Falling back to filtering posts for pricing data');
            
            // First filter by time range
            const now = new Date();
            const timeFilteredPosts = (posts || []).filter(post => {
                if (!post?.time) return false;
                if (timeRange === 'all') return true;
                const date = new Date(post.time * 1000);
                const years = parseInt(timeRange);
                return date >= subYears(now, years);
            });

            // Then filter by selected product and extract pricing assessments
            const filteredPosts = selectedProduct ? filterPostsByProduct(timeFilteredPosts) : timeFilteredPosts;
            
            // Map the filtered posts to include only pricing assessment data
            const pricingData = filteredPosts
                .map(post => {
                    if (!post?.post_assessments) return null;
                    const assessment = post.post_assessments.find(assessment => assessment?.type === 'pricing');
                    if (assessment) {
                        return {
                            ...post,
                            rating: assessment.rating,
                            confidence: assessment.confidence,
                            explanation: assessment.explanation
                        };
                    }
                    return null;
                })
                .filter(item => item !== null);

            setData(pricingData);
        }
    }, [assessmentData, posts, selectedProduct, timeRange]); // eslint-disable-line react-hooks/exhaustive-deps

    const chartDataMemo = useMemo(() => {
        console.log('Recalculating chart data with:', {
            dataLength: data.length,
            selectedProduct
        });
        
        if (data.length === 0) return null;

        const categoryCounts = {
            veryReceptive: 0,
            moderatelyReceptive: 0,
            neutral: 0,
            notReceptive: 0,
            extremelyNotReceptive: 0
        };

        // Count items in each category
        data.forEach(item => {
            const rating = item.rating;
            if (rating !== null) {
                if (rating > thresholds.veryReceptive) {
                    categoryCounts.veryReceptive++;
                } else if (rating > thresholds.moderatelyReceptive) {
                    categoryCounts.moderatelyReceptive++;
                } else if (rating > thresholds.notReceptive) {
                    categoryCounts.neutral++;
                } else if (rating > thresholds.extremelyNotReceptive) {
                    categoryCounts.notReceptive++;
                } else {
                    categoryCounts.extremelyNotReceptive++;
                }
            }
        });

        // Log category counts for debugging
        console.log('Category counts:', categoryCounts);

        const total = Object.values(categoryCounts).reduce((a, b) => a + b, 0);

        // Calculate the maximum percentage on either side
        const maxPercentage = Math.max(
            (categoryCounts.veryReceptive + categoryCounts.moderatelyReceptive) / total * 100,
            (categoryCounts.extremelyNotReceptive + categoryCounts.notReceptive) / total * 100
        );
        
        // Round up to nearest 10%
        const axisLimit = Math.ceil(maxPercentage / 10) * 10;

        return {
            categoryCounts,
            axisLimit,
            chartData: {
                labels: [''],
                datasets: [
                    {
                        label: 'Very Receptive',
                        data: [-(categoryCounts.veryReceptive / total) * 100],
                        backgroundColor: colors.veryReceptive,
                        stack: 'left'
                    },
                    {
                        label: 'Moderately Receptive',
                        data: [-(categoryCounts.moderatelyReceptive / total) * 100],
                        backgroundColor: colors.moderatelyReceptive,
                        stack: 'left'
                    },
                    {
                        label: 'Extremely Not Receptive',
                        data: [+(categoryCounts.extremelyNotReceptive / total) * 100],
                        backgroundColor: colors.extremelyNotReceptive,
                        stack: 'right'
                    },
                    {
                        label: 'Not Receptive',
                        data: [+(categoryCounts.notReceptive / total) * 100],
                        backgroundColor: colors.notReceptive,
                        stack: 'right'
                    }
                ]
            }
        };
    }, [data, thresholds, colors, selectedProduct]);

    useEffect(() => {
        setChartData(chartDataMemo?.chartData || null);
    }, [chartDataMemo]);

    const handleBarClick = (event, elements) => {
        if (elements.length > 0) {
            const { datasetIndex } = elements[0];
            
            // Map datasetIndex to categories based on our updated chart order
            let selectedCategory;
            switch (datasetIndex) {
                case 0: // First bar (leftmost)
                    selectedCategory = 'veryReceptive';
                    break;
                case 1:
                    selectedCategory = 'moderatelyReceptive';
                    break;
                case 2:
                    selectedCategory = 'extremelyNotReceptive';
                    break;
                case 3:
                    selectedCategory = 'notReceptive';
                    break;
                default:
                    selectedCategory = null;
            }

            setSelectedCategory(selectedCategory);

            if (selectedCategory) {
                // Filter based on whether we're using assessment data or posts
                let categoryPosts;
                
                if (assessmentData) {
                    // Filter the assessment data directly
                    categoryPosts = data.filter(item => {
                        if (!item || item.rating === null) return false;

                        const rating = item.rating;
                        switch (selectedCategory) {
                            case 'veryReceptive':
                                return rating > thresholds.veryReceptive;
                            case 'moderatelyReceptive':
                                return rating > thresholds.moderatelyReceptive && rating <= thresholds.veryReceptive;
                            case 'neutral':
                                return rating > thresholds.notReceptive && rating <= thresholds.moderatelyReceptive;
                            case 'notReceptive':
                                return rating > thresholds.extremelyNotReceptive && rating <= thresholds.notReceptive;
                            case 'extremelyNotReceptive':
                                return rating <= thresholds.extremelyNotReceptive;
                            default:
                                return false;
                        }
                    });
                } else {
                    // Get posts for the selected product (or all posts if no product selected)
                    const productPosts = selectedProduct ? filterPostsByProduct(posts) : posts;
                    
                    // Then filter by category
                    categoryPosts = productPosts
                        .filter(post => {
                            const assessment = post.post_assessments.find(a => a.type === 'pricing');
                            if (!assessment || assessment.rating === null) return false;

                            const rating = assessment.rating;
                            switch (selectedCategory) {
                                case 'veryReceptive':
                                    return rating > thresholds.veryReceptive;
                                case 'moderatelyReceptive':
                                    return rating > thresholds.moderatelyReceptive && rating <= thresholds.veryReceptive;
                                case 'neutral':
                                    return rating > thresholds.notReceptive && rating <= thresholds.moderatelyReceptive;
                                case 'notReceptive':
                                    return rating > thresholds.extremelyNotReceptive && rating <= thresholds.notReceptive;
                                case 'extremelyNotReceptive':
                                    return rating <= thresholds.extremelyNotReceptive;
                                default:
                                    return false;
                            }
                        })
                        .map(post => {
                            const assessment = post.post_assessments.find(a => a.type === 'pricing');
                            return {
                                ...post,
                                rating: assessment.rating,
                                confidence: assessment.confidence,
                                explanation: assessment.explanation,
                                ratingName: 'pricing',
                                ratingType: 'pricing'
                            };
                        });
                }

                // Ensure all posts have the necessary fields for display
                const formattedPosts = categoryPosts.map(post => ({
                    ...post,
                    ratingName: 'pricing',
                    ratingType: 'pricing'
                }));

                setPostsReferenced(formattedPosts);
            }
        } else {
            setSelectedCategory(null);
            setPostsReferenced([]);
        }
    };

    const handleClosePostsReferenced = () => {
        setPostsReferenced([]);
        setSelectedCategory(null);
    };

    const handleSummaryClick = (categories) => {
        let categoryPosts;
        
        if (assessmentData) {
            // Filter the assessment data directly
            categoryPosts = data.filter(item => {
                if (!item || item.rating === null) return false;

                const rating = item.rating;
                return categories.some(category => {
                    switch (category) {
                        case 'veryReceptive':
                            return rating > thresholds.veryReceptive;
                        case 'moderatelyReceptive':
                            return rating > thresholds.moderatelyReceptive && rating <= thresholds.veryReceptive;
                        case 'neutral':
                            return rating > thresholds.notReceptive && rating <= thresholds.moderatelyReceptive;
                        case 'notReceptive':
                            return rating > thresholds.extremelyNotReceptive && rating <= thresholds.notReceptive;
                        case 'extremelyNotReceptive':
                            return rating <= thresholds.extremelyNotReceptive;
                        default:
                            return false;
                    }
                });
            });
        } else {
            // Get posts for the selected product (or all posts if no product selected)
            const productPosts = selectedProduct ? filterPostsByProduct(posts) : posts;

            // Then filter by categories
            categoryPosts = productPosts.filter(post => {
                const assessment = post.post_assessments.find(a => a.type === 'pricing');
                if (!assessment || assessment.rating === null) return false;

                const rating = assessment.rating;
                return categories.some(category => {
                    switch (category) {
                        case 'veryReceptive':
                            return rating > thresholds.veryReceptive;
                        case 'moderatelyReceptive':
                            return rating > thresholds.moderatelyReceptive && rating <= thresholds.veryReceptive;
                        case 'neutral':
                            return rating > thresholds.notReceptive && rating <= thresholds.moderatelyReceptive;
                        case 'notReceptive':
                            return rating > thresholds.extremelyNotReceptive && rating <= thresholds.notReceptive;
                        case 'extremelyNotReceptive':
                            return rating <= thresholds.extremelyNotReceptive;
                        default:
                            return false;
                    }
                });
            }).map(post => {
                const assessment = post.post_assessments.find(a => a.type === 'pricing');
                return {
                    ...post,
                    rating: assessment.rating,
                    confidence: assessment.confidence,
                    explanation: assessment.explanation,
                    ratingName: 'pricing',
                    ratingType: 'pricing'
                };
            });
        }
        
        // Ensure all posts have the necessary fields for display
        const formattedPosts = categoryPosts.map(post => ({
            ...post,
            ratingName: 'pricing',
            ratingType: 'pricing'
        }));

        setPostsReferenced(formattedPosts);
        setSelectedCategory(categories.join('-'));
    };

    // Add an effect to log product selection changes
    useEffect(() => {
        console.log('Selected product changed:', {
            productId: selectedProduct, 
            productName: selectedProduct && topicMap && topicMap[selectedProduct] 
                ? topicMap[selectedProduct].name 
                : 'unknown'
        });
    }, [selectedProduct]); // eslint-disable-line react-hooks/exhaustive-deps

    // Select a default product if none is selected yet. Prefers the
    // page-wide shared topic selection (if valid for one of this
    // component's own topic maps); otherwise falls back to the
    // highest-priority topic with pricing data. Combined into a single
    // effect (rather than two competing ones) so there's no race where
    // this component's own default could overwrite a valid incoming
    // shared selection, or vice versa.
    useEffect(() => {
        if (selectedProduct) return;

        if (sharedSelectedProduct) {
            const validHere = productTopicsForSelector[sharedSelectedProduct]
                || topicMap?.[sharedSelectedProduct]
                || filteredTopicMap[sharedSelectedProduct];
            if (validHere) {
                setSelectedProduct(sharedSelectedProduct);
                return;
            }
        }

        if (Object.keys(productTopicsForSelector).length > 0) {
            const sortedTopics = Object.values(productTopicsForSelector).sort((a, b) => {
                const priorityA = a.priority === undefined ? Infinity : a.priority;
                const priorityB = b.priority === undefined ? Infinity : b.priority;
                return priorityA - priorityB; // Sort by priority (lowest first)
            });

            if (sortedTopics[0]) {
                console.log('Auto-selecting first topic with pricing data:', sortedTopics[0]);
                setSelectedProduct(sortedTopics[0].id);
            }
        }
    }, [productTopicsForSelector, selectedProduct, sharedSelectedProduct, topicMap, filteredTopicMap]);

    return (
        <div className="pricing-plot">
            {console.log("PricingPlot rendering with:", {
                topicMapValid: hasValidProductData(topicMap),
                filteredTopicMapValid: hasValidProductData(filteredTopicMap),
                selectedProduct,
                dataLength: data.length
            })}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <div>
                    {/* Use productTopicsForSelector for filtered topics with pricing data */}
                    {hasValidProductData(productTopicsForSelector) ? (
                        <>
                            {console.log("Using filtered topics with pricing data for ProductSelector")}
                            <ProductSelector
                                topicMap={productTopicsForSelector}
                                selectedId={selectedProduct}
                                onSelect={(newProductId) => {
                                    console.log("Product selected from UI:", newProductId);
                                    selectProduct(newProductId);
                                }}
                                className="product-selector"
                                label="Product"
                            />
                        </>
                    ) : hasValidProductData(topicMap) ? (
                        <>
                            {console.log("Using main topicMap for ProductSelector")}
                            <ProductSelector
                                topicMap={topicMap}
                                selectedId={selectedProduct}
                                onSelect={(newProductId) => {
                                    console.log("Product selected from UI:", newProductId);
                                    selectProduct(newProductId);
                                }}
                                className="product-selector"
                                label="Product"
                            />
                        </>
                    ) : hasValidProductData(filteredTopicMap) ? (
                        <>
                            {console.log("Using filteredTopicMap for ProductSelector")}
                            <ProductSelector
                                topicMap={filteredTopicMap}
                                selectedId={selectedProduct}
                                onSelect={(newProductId) => {
                                    console.log("Product selected from UI:", newProductId);
                                    selectProduct(newProductId);
                                }}
                                className="product-selector"
                                label="Product"
                            />
                        </>
                    ) : (
                        <div>No product data available</div>
                    )}
                </div>
                <div>
                    <TimeRangeSelector
                        timeRange={timeRange}
                        onTimeRangeChange={setTimeRange}
                    />
                </div>
            </div>
            {chartData && chartDataMemo && chartDataMemo.categoryCounts ? (
                <>
                    <PricingSummary 
                        categoryCounts={chartDataMemo.categoryCounts} 
                        onCategoryClick={handleSummaryClick}
                    />
                    <div className="chart-container">
                        <div className="chart-inner">
                            <Bar
                                data={chartData}
                                options={{
                                    animation: false,  // Disable all animations
                                    indexAxis: 'y',
                                    responsive: true,
                                    maintainAspectRatio: false,
                                    layout: {
                                        padding: {
                                            left: 10,
                                            right: 10
                                        }
                                    },
                                    scales: {
                                        x: { 
                                            stacked: true,
                                            title: { 
                                                display: true, 
                                                text: 'Distribution of Customer Sentiment (%)',
                                                font: {
                                                    size: 13,
                                                    weight: 500
                                                },
                                                padding: { top: 10 }
                                            },
                                            grid: {
                                                color: (context) => context.tick.value === 0 ? 'rgba(0,0,0,0.5)' : 'rgba(0,0,0,0.1)',
                                                lineWidth: (context) => context.tick.value === 0 ? 2 : 1,
                                            },
                                            ticks: {
                                                callback: (value) => `${Math.abs(value)}%`
                                            },
                                            min: -chartDataMemo.axisLimit,
                                            max: chartDataMemo.axisLimit,
                                        },
                                        y: { 
                                            stacked: true,
                                            grid: {
                                                display: false
                                            },
                                            display: false  // Hide y-axis since we only have one bar
                                        },
                                    },
                                    plugins: {
                                        tooltip: {
                                            callbacks: {
                                                label: function(context) {
                                                    const value = Math.abs(context.parsed.x);
                                                    return `${context.dataset.label}: ${value.toFixed(1)}% of responses`;
                                                }
                                            }
                                        },
                                        legend: {
                                            position: 'bottom',
                                            labels: {
                                                padding: 20,
                                                font: {
                                                    size: 12
                                                },
                                                filter: (legendItem, data) => {
                                                    if (legendItem.text === 'Neutral') {
                                                        return data.datasets.findIndex(d => 
                                                            d.backgroundColor === colors.neutral
                                                        ) === legendItem.datasetIndex;
                                                    }
                                                    return true;
                                                }
                                            }
                                        }
                                    },
                                    onClick: handleBarClick,
                                    barThickness: 60,
                                }}
                            />
                        </div>
                    </div>
                </>
            ) : (
                <div className="no-pricing-data-message">
                    <p>No pricing data available for the selected product. Please select a different product or adjust your filters.</p>
                </div>
            )}
            
            {/* Add the competitor comparison chart if we have topic map data */}
            {topicMap && hasValidProductData(topicMap) && (
                <div className="competitor-comparison-section">
                    <PricingComparisonChart 
                        topicMap={topicMap} 
                        selectedProductId={selectedProduct || ""}
                    />
                </div>
            )}
            
            <FilteredPostsDisplay 
                posts={postsReferenced}
                onClose={handleClosePostsReferenced}
                id="pricing-posts"
                title={'Posts Referenced'}
                subtitle={selectedCategory ? selectedCategory.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase()) : ''}
                ratingDisplayName="Pricing Receptiveness"
                ratingColorScheme={{
                    low: 0,  // ratings <= 0 will be red
                    high: 4  // ratings > 4 will be green, ratings between 0-4 will be yellow
                }}
                autoScroll={true}
                query={getAssessmentKeywords('pricing')}
            />
        </div>
    );
};

export default PricingPlot;